import { forwardRef, ReactNode } from 'react';
import * as Radix from '@radix-ui/react-radio-group';
import { cn } from '../lib/cn';

export const RadioGroup = forwardRef<
  React.ElementRef<typeof Radix.Root>,
  React.ComponentPropsWithoutRef<typeof Radix.Root>
>(function RadioGroup({ className, ...rest }, ref) {
  return (
    <Radix.Root
      ref={ref}
      className={cn('flex flex-col gap-2.5', className)}
      {...rest}
    />
  );
});

interface RadioItemProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Radix.Item>, 'children'> {
  label: ReactNode;
  description?: ReactNode;
}

export const RadioItem = forwardRef<
  React.ElementRef<typeof Radix.Item>,
  RadioItemProps
>(function RadioItem({ label, description, className, id, value, ...rest }, ref) {
  return (
    <label
      htmlFor={id ?? value}
      className="flex cursor-pointer items-start gap-2.5 select-none"
    >
      <Radix.Item
        ref={ref}
        id={id ?? value}
        value={value}
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border-strong bg-surface',
          'transition-[border-color,box-shadow] duration-fast ease-enter',
          'data-[state=checked]:border-primary',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      >
        <Radix.Indicator className="block h-2.5 w-2.5 rounded-full bg-primary" />
      </Radix.Item>
      <div className="min-w-0">
        <span className="block text-base text-ink leading-ui">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-sm text-ink-secondary">
            {description}
          </span>
        ) : null}
      </div>
    </label>
  );
});
